import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";
import { selectIsAuthed } from "../../../selectors/userSelectors";

import { fetchDashboardSummary } from "../api/summary";
import { DashboardSummaryView } from "./DashboardSummaryView";

function DashboardScreenContent() {
  const isAuthed = useSelector(selectIsAuthed);
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [staleSummary, setStaleSummary] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const requestIdRef = useRef(0);
  const summaryRef = useRef(null);

  useEffect(() => {
    summaryRef.current = summary;
  }, [summary]);

  const loadSummary = useCallback(async ({ isRefresh = false } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!isRefresh) {
      setLoadState("loading");
      setLoadError(null);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await fetchDashboardSummary();
      if (requestIdRef.current !== requestId) return;
      setSummary(data);
      setStaleSummary(null);
      setLoadError(null);
      setLoadState("loaded");
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      const message = messageFromError(err);
      setLoadError(message);
      if (summaryRef.current) {
        setStaleSummary(summaryRef.current);
        setLoadState("stale");
      } else {
        setLoadState("error");
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      navigate("/login", { replace: true });
      return undefined;
    }
    void loadSummary();
    return undefined;
  }, [isAuthed, navigate, loadSummary]);

  const handleRetry = () => {
    void loadSummary({ isRefresh: Boolean(summary || staleSummary) });
  };

  if (!isAuthed) {
    return null;
  }

  if (loadState === "loading" || loadState === "idle") {
    return (
      <div className="feature-dashboard__state">
        <Spinner size="lg" label="Завантаження огляду" />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-dashboard__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити огляд"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
          primaryAction={(
            <button type="button" className="action-btn" onClick={handleRetry}>
              Спробувати знову
            </button>
          )}
        />
      </div>
    );
  }

  const activeSummary = summary || staleSummary;

  return (
    <div className="feature-dashboard">
      <section className="clients-overview" aria-label="Огляд панелі">
        <header className="clients-header">
          <div className="clients-header__text">
            <h1 className="clients-header__title">Огляд</h1>
            <p className="clients-header__sub">
              Зведені показники з бекенду. Оновіть сторінку, щоб отримати актуальні дані.
            </p>
          </div>
          <div className="clients-header__actions">
            <button
              type="button"
              className="action-btn"
              onClick={() => void loadSummary({ isRefresh: true })}
              disabled={refreshing}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true" />
              {refreshing ? "Оновлення…" : "Оновити"}
            </button>
          </div>
        </header>
      </section>

      {loadState === "stale" ? (
        <div className="feature-dashboard__stale-banner" role="status">
          <span>
            Не вдалося оновити дані: {loadError || "помилка мережі"}.
            Показано попередні значення.
          </span>
          <button type="button" className="action-btn" onClick={handleRetry}>
            Спробувати знову
          </button>
        </div>
      ) : null}

      {activeSummary ? <DashboardSummaryView summary={activeSummary} /> : null}
    </div>
  );
}

export default DashboardScreenContent;
